import React, { useEffect, useRef } from 'react';
import { useWorldStore } from '../state/worldStore';
import { PerformanceMonitor } from '@react-three/drei';
import { IS_MOBILE } from '../config';

// AdaptiveResolution — trade pixels for frames, and only when frames are short.
//
// Desktop renders at up to 2x device pixels. On a strong GPU that is free
// sharpness; on an integrated one driving a retina or 4K panel it is FOUR times
// the fill work of 1x, and the whole world simply runs at 30fps. The premium
// target says keep the sharpness wherever the machine can hold it — so this
// starts at the display's own ratio (capped at 2), steps DOWN when the frame
// rate sags and back UP when it recovers, never below 1x.
//
// Phones stay at their fixed 1x: that ceiling is about iOS memory (a 3x drawing
// buffer is ~9x the VRAM), not frame rate, and it is not this component's call.
//
// ?dpr=<n> pins a value for testing; ?dpr=auto (the default) adapts.
const q = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('dpr') : null;
export const PINNED_DPR = q && q !== 'auto' && Number.isFinite(parseFloat(q)) ? parseFloat(q) : null;
export const MAX_DPR = typeof window !== 'undefined' ? Math.min(window.devicePixelRatio || 1, 2) : 1;
export const START_DPR = PINNED_DPR ?? (IS_MOBILE ? 1 : MAX_DPR);

// BEHIND THE LANDING PAGE the world is a backdrop: dimmed, under a gradient and
// a headline, and rendering the whole time somebody reads the pitch. Full
// resolution there is spent on pixels nobody can resolve. It renders at 1x on
// desktop (0.75x on phones) until Enter, then hands back to the monitor.
const LANDING_DPR = IS_MOBILE ? 0.75 : 1;

export default function AdaptiveResolution() {
  const setDpr = useWorldStore((s) => s.setRenderDpr);
  const entered = useWorldStore((s) => s.entered);
  const wanted = useRef(START_DPR);   // what the monitor would like, in-world
  const adaptive = !IS_MOBILE && PINNED_DPR == null && MAX_DPR > 1;

  useEffect(() => {
    if (PINNED_DPR != null) return;
    setDpr(entered ? wanted.current : Math.min(LANDING_DPR, wanted.current));
  }, [entered, setDpr]);

  if (!adaptive) return null;
  const apply = (next) => {
    wanted.current = next;
    if (useWorldStore.getState().entered) setDpr(next);
  };
  return (
    <PerformanceMonitor
      factor={1}
      flipflops={4}
      // Quarter steps: a continuous value would resize the drawing buffer on
      // every wobble of the average, and each resize is a full reallocation.
      onChange={({ factor }) => apply(Math.max(1, Math.round((1 + (MAX_DPR - 1) * factor) * 4) / 4))}
      onFallback={() => apply(1)}
    />
  );
}
